import { Injectable, NgZone } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  constructor(
    public snackBar: MatSnackBar,
    private zone: NgZone
  ) {
    window.$notification = this;
  }

  private _open(message: string, isError: boolean) {
    // Had an issue with the snackbar being ran outside of angular's zone.
    this.zone.run(() => {
      this.snackBar.open(message, isError ? 'X' : 'OK', {
        duration: 5000,
        horizontalPosition: 'right',
        panelClass: isError ? ['notification-bottom-margin', 'error'] : ['notification-bottom-margin']
      });
    });
  }

  showSuccess(message: string): void {
    this._open(message, false);
  }

  // ---- App notifications (host.notify) -------------------------------------
  // A toast while the page is visible; a browser Notification when it is hidden
  // (permission is requested by the platform on the first call, never by the
  // app). Rate-limited per app: one per tag every 5 s, 10 per minute.
  private readonly _appLastByTag = new Map<string, number>();
  private readonly _appMinute = new Map<string, number[]>();

  notifyFromApp(appName: string, opts: { title: string; body?: string; tag?: string; onClick?: () => void }): void {
    const now = Date.now();
    const tagKey = `${appName}:${opts.tag || opts.title}`;
    const last = this._appLastByTag.get(tagKey) || 0;
    if (now - last < 5000) return;
    const recent = (this._appMinute.get(appName) || []).filter(t => now - t < 60000);
    if (recent.length >= 10) { console.debug(`[notify] ${appName}: rate limited`); return; }
    recent.push(now);
    this._appMinute.set(appName, recent);
    this._appLastByTag.set(tagKey, now);

    const text = opts.body ? `${opts.title} — ${opts.body}` : opts.title;
    if (document.visibilityState === 'hidden' && 'Notification' in window) {
      const show = () => {
        try {
          const n = new Notification(opts.title, { body: opts.body, tag: opts.tag ? tagKey : undefined });
          n.onclick = () => { window.focus(); n.close(); opts.onClick?.(); };
        } catch { this._toast(text, opts.onClick); }
      };
      if (Notification.permission === 'granted') { show(); return; }
      if (Notification.permission === 'default') {
        Notification.requestPermission().then(p => p === 'granted' ? show() : this._toast(text, opts.onClick));
        return;
      }
    }
    this._toast(text, opts.onClick);
  }

  private _toast(text: string, onClick?: () => void): void {
    this.zone.run(() => {
      const ref = this.snackBar.open(text, onClick ? 'Open' : 'OK', {
        duration: 6000,
        horizontalPosition: 'right',
        panelClass: ['notification-bottom-margin']
      });
      if (onClick) ref.onAction().subscribe(() => onClick());
    });
  }

  showError(message: string): void {
    this._open(message, true);
  }
}
